// Acc Dist AI (tombol AI): results computed server-side (backend/ai/accdist), served by GET /api/accdist-ai/latest.
// The model and calibration never reach the browser — only per-stock results and the patterns matched that day.

export type AiFactor = [string, number]; // [factor group, percentage points]

export interface AiStock {
  p: number;          // calibrated probability (0..1)
  rk?: number;        // within-day rank of the model score in its liquidity group (0..1)
  tk?: number;        // tingkat 0..4 (band of rk); older results: derived from rk
  liq: boolean;
  price: number;
  base?: number;      // average probability of the stock's liquidity group today (start of the explanation)
  pts?: number[];     // points per factor group (AccDistAiResults.factor_groups); base + Σ pts + rest = p
  rest?: number;      // remainder (points)
  ar?: (number | null)[];  // method 'avg': each factor's rank among today's stocks, 0..100 (higher = better)
  tl?: string;        // Acc Dist pattern of the 9 menu columns: KAK / KAM / EA / N / ED / KDM / KDK
  tv?: number[];      // the 9 Acc Dist values W4..D0 behind it (Rp juta, unweighted)
  dv?: (number | null)[];  // D10..D0 (AccDistAiResults.daily.dates): Acc Dist % per day (menu % view); null = no trade
  dc?: (number | null)[];  // D10..D0: price change % per day (close vs the previous close)
  kp?: number | null;           // Prob from the stock's own averages (0.01..0.99), see AccDistAiResults.own
  kx?: (number | null)[];       // per own.params: today's value
  km?: (number | null)[];       // per own.params: the stock's own 60-day average
  kq?: (number | null)[];       // per own.params: weighted 0..1 position over the 9 columns
  kg?: (number | null)[][];     // positions grid: 9 columns (own.periods, W4..D0) × own.params
  hh?: number | null;           // kebiasaan saham ini: share of its own earlier days that hit the target
  hn?: number;                  // number of those days
  up?: AiFactor[];    // results before factor groups: biggest factors up / down
  down?: AiFactor[];
  pn: string[];       // matched "pola naik" ids
  pw: string[];       // matched "pola waspada" ids
}

export interface AiPattern {
  kind: 'naik' | 'waspada';
  conds: string[];
  bandar: boolean;
  hit: number;        // hit rate in the holdout period
  ctx: number;        // similar stocks in the same period
  excess: number;
  n: number;
  stocks: number;
}

// Internal monitoring (GET /api/accdist-ai/monitor, backend/ai/accdist/monitor.py): every published day's calls and
// the High of the next 3 trading days (vs the day's close); h0 = D0's High vs the close of D-1 and d0 = 1 when that
// reached +5% (absent on older files). Row fields follow AiMonitor.fields.
export type AiMonitorRow = [
  p: number | null, kp: number | null, tk: number | null, rk: number | null, tl: string | null, liq: boolean,
  close: number, h1: number | null, h2: number | null, h3: number | null, ho: number | null, hit: 0 | 1 | null,
  h0?: number | null, d0?: 0 | 1,
];

export interface AiMonitorDay {
  date: string;              // signal date (YYYYMMDD)
  days_passed: number;       // trading days after it so far (0..3)
  method?: string | null;    // 'avg' | 'model' (older results: none)
  target?: string | null;    // the target text shown that day
  recomputed?: boolean;      // recomputed later with the current scheme (only what was known that day), not what was shown
  stocks: Record<string, AiMonitorRow>;
  // IHSG (ihsg.py): closes of D-1, D0, D+1, D+2, D+3 (null = not there yet) and p = the chance that IHSG closes D+1
  // above D0, said the next morning before IDX opens (null until then / no prediction); absent on older files
  // w = still waiting for that night's US session (p comes the next morning)
  ihsg?: { c: (number | null)[]; p: number | null; w?: boolean } | null;
}

export interface AiMonitor {
  generated_at: string;
  target: string;
  up: number;                // 0.05
  horizon: number;           // 3
  fields: string[];
  dates: AiMonitorDay[];     // newest first
}

// IHSG time-series study (backend/ai/accdist/ihsg_ts.py, GET /api/accdist-ai/monitor/ihsg-ts): r = IHSG return %,
// F = foreign net buy (Rp miliar), D = domestic value traded (Rp triliun), regular board. Split parts are
// [first target date, last target date, rows]; per-split numbers are keyed train / val / test.
export type AiTsSplit = 'train' | 'val' | 'test';
export interface AiIhsgTsRolling {
  r_hat: (number | null)[]; var_hat: (number | null)[]; F_hat: (number | null)[]; D_hat: (number | null)[];
  acc_test: number | null; acc_var_test: number | null; acc_all: number | null; rmse_test: number | null;
}
export interface AiIhsgTs {
  generated_at: string;
  board: string;
  series: { dates: string[]; close: (number | null)[]; r: (number | null)[]; F: (number | null)[]; D: (number | null)[] };
  p: number;                                   // days of history used, picked on val
  p_scores: { p: number; val_rmse: number | null; val_acc: number | null }[];
  window: number;                              // rolling window, rows
  split: Record<AiTsSplit, [string, string, number]>;
  models: { key: string; name: string; acc: Record<AiTsSplit, number | null>; rmse: Record<AiTsSplit, number | null> }[];
  stage1: Record<'F' | 'D', { rmse: Record<AiTsSplit, number | null>; rmse_naive: Record<AiTsSplit, number | null>; acc: Record<AiTsSplit, number | null> }>;
  rolling: { dates: string[]; r: (number | null)[]; F: (number | null)[]; D: (number | null)[]; expanding: AiIhsgTsRolling; window: AiIhsgTsRolling };
  coef: Record<'F' | 'D' | 'r' | 'var', { names: string[]; values: (number | null)[] }>;
  next: { after: string; F_hat: number; D_hat: number; r_hat: number; p_up: number | null } | null;  // null: last days not consecutive
}

export interface AccDistAiResults {
  date: string;              // YYYYMMDD (= Acc Dist D0)
  generated_at: string;
  model_version: string;
  target: string;
  base_rate: number;
  method?: 'avg' | 'model';  // where p comes from: the average of a few ranks, or the LightGBM model
  liquid_value?: number;     // Rp/day (5-day average) from which a stock counts as liquid
  walk_forward: { months: [string, string]; n: number; base: number; auc: number; top10_hit: number; top10_hit_liquid: number };
  levels?: {
    names: string[];  // tingkat 0..4
    months: [string, string];
    hits: Record<'liquid' | 'other', { hit: number; n: number }[]>;  // realised hit rate per tingkat (walk-forward)
  };
  factor_groups?: { name: string; acc: boolean }[];  // acc = Acc Dist parameter, else the stock's character/technicals
  shapes?: {
    order: string[];                 // most accumulation → most distribution
    names: Record<string, string>;
    desc: Record<string, string>;
    columns: string[];               // W4 .. D0
  };
  daily?: { dates: string[] };       // D10 .. D0 (YYYYMMDD) of AiStock.dv / dc
  own?: {
    params: { key: string; name: string }[];
    window: number;                           // trading days of the stock's own average (D columns)
    week_window?: number;                     // weeks of the stock's own average (W columns)
    periods?: string[];                       // W4 .. D0
    weights?: number[];                       // 0.2 .. 1.0
    months: [string, string];                 // test months of `hits`
    hits: { hit: number | null; n: number }[];  // what actually happened per Prob band 0–10% .. 90–100%
    base?: number;                             // all-stock hit rate on the same rows and months as `hits`
    compat?: { keys: string[]; auc: number[]; w: number[] };  // per parameter: link to the target (daily AUC) → weight
  };
  habit?: { all: number; since: string; min_days: number };  // all-stock rate on the same days; shown from min_days
  pattern_periods: { discovery: [string, string]; holdout: [string, string] };
  patterns: Record<string, AiPattern>;
  stocks: Record<string, AiStock>;
  skipped?: Record<string, string>;   // not rated (outside what the model learned) → reason text
  market_warning?: string | null;     // e.g. many stocks locked at ARB/ARA on the day
}

/** Tingkat = the stock's rank among the day's stocks of its liquidity group, in fifths (4 = top 20%). */
export const AI_LEVEL_NAMES = ['Sangat rendah', 'Rendah', 'Sedang', 'Cukup tinggi', 'Tinggi'];
export const AI_LEVEL_COLOR = ['text-[#ff0000]', 'text-[#ffa500]', 'text-gray-300', 'text-[#b2ff00]', 'text-[#00ff00]'];
export const aiLevel = (s: AiStock) => s.tk ?? Math.min(4, Math.floor((s.rk ?? 0.5) * 5));
export const aiLevelName = (ai: AccDistAiResults | null, k: number) => ai?.levels?.names[k] ?? AI_LEVEL_NAMES[k] ?? '';

/** Acc Dist pattern (the AI column), e.g. "Konsisten Akumulasi Kuat". */
export const AI_SHAPE_ORDER = ['KAK', 'KAM', 'EA', 'N', 'ED', 'KDM', 'KDK'];
export const AI_SHAPE_NAMES: Record<string, string> = {
  KAK: 'Konsisten Akumulasi Kuat', KAM: 'Konsisten Akumulasi Melemah', EA: 'Early Akumulasi', N: 'Netral',
  ED: 'Early Distribusi', KDM: 'Konsisten Distribusi Melemah', KDK: 'Konsisten Distribusi Kuat',
};
export const aiShapeName = (ai: AccDistAiResults | null, s: AiStock) => (s.tl ? ai?.shapes?.names[s.tl] ?? s.tl : '');
/** Short form for the narrow Summary column: "Konsisten Akum Melemah", "Early Dist". */
export const aiShapeShort = (name: string) => name.replace(/Akumulasi/g, 'Akum').replace(/Distribusi/g, 'Dist');
/** Higher = more accumulation (sorting). */
export const aiShapeRank = (s: AiStock) => (s.tl ? AI_SHAPE_ORDER.length - AI_SHAPE_ORDER.indexOf(s.tl) : 0);
const SHAPE_HEX: Record<string, string> = {
  KAK: '#00ff00', KAM: '#7dff4d', EA: '#b2ff00', N: '#9ca3af', ED: '#ffa500', KDM: '#ff6a00', KDK: '#ff0000',
};
export const aiShapeColor = (code?: string) => SHAPE_HEX[code ?? ''] ?? '#9ca3af';
/** Color token for the column filter (ijo/lime/netral/oren/merah). */
export const aiShapeFilterColor = (code?: string) =>
  code === 'KAK' || code === 'KAM' ? 'text-[#00ff00]' : code === 'EA' ? 'text-[#b2ff00]' : code === 'ED' ? 'text-[#ffa500]'
    : code === 'KDM' || code === 'KDK' ? 'text-[#ff0000]' : 'text-gray-500';

/** Cached AI results older than this are fetched again when the AI button is switched on. */
export const AI_REFRESH_MS = 5 * 60 * 1000;

/** "!" in the Prob cell only for strong warnings (≥ 8 points below similar stocks); all matches are in the detail. */
export const AI_STRONG_WARNING = -0.08;
export const hasStrongWarning = (s: AiStock, patterns: Record<string, AiPattern>) =>
  s.pw.some(id => (patterns[id]?.excess ?? 0) <= AI_STRONG_WARNING);

/** Same trading day? `ymd` = YYYYMMDD; `label` = Acc Dist date as "20 Oct" or "2026-10-20". */
export const sameAccDistDate = (ymd: string, label: string) => {
  if (!ymd || !label) return true;
  if (/^\d{4}-\d{2}-\d{2}$/.test(label)) return label.replace(/-/g, '') === ymd;
  const m = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const short = `${parseInt(ymd.slice(6), 10)} ${m[parseInt(ymd.slice(4, 6), 10) - 1]}`;
  return /^\d{1,2} [A-Z][a-z]{2}$/.test(label.trim()) ? label.trim() === short : true;
};

/** Own-average Prob color: position vs the stock's own normal. */
export const aiKpColor = (q: number) =>
  q >= 0.8 ? 'text-[#00ff00]' : q >= 0.6 ? 'text-[#b2ff00]' : q <= 0.2 ? 'text-[#ff0000]' : q <= 0.4 ? 'text-[#ffa500]' : 'text-gray-300';

/** Peluang (naik) color, the Acc Dist column and dialog: relative to the average stock (base rate). */
export const aiProbColor = (p: number, base: number) =>
  p >= base + 0.15 ? 'text-[#00ff00]' : p >= base + 0.05 ? 'text-[#b2ff00]' : p <= base - 0.08 ? 'text-[#ff0000]'
    : p <= base - 0.03 ? 'text-[#ffa500]' : 'text-gray-300';

const MONTHS_ID = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];

export const fmtYmd = (d: string) =>
  d && d.length === 8 ? `${parseInt(d.slice(6), 10)} ${MONTHS_ID[parseInt(d.slice(4, 6), 10) - 1]} ${d.slice(0, 4)}` : d;

/** YYYYMM → "Mei 2026". */
export const fmtYm = (d: string) => (d && d.length === 6 ? `${MONTHS_ID[parseInt(d.slice(4, 6), 10) - 1]} ${d.slice(0, 4)}` : d);
